import type { SessionUser } from "@livcg/nodecg-react";
import { ShieldCheck, Tv } from "lucide-react";
import { cn } from "../ui/cn.js";

type Workspace = "match-day" | "admin";

type Props = {
  active: Workspace;
  user: SessionUser | null;
};

const URLS: Record<Workspace, string> = {
  "match-day": "/livcg",
  admin: "/livcg/admin",
};

/** Two-tab switcher between Match Day and Admin. Admin tab only shows for
 *  admin-role users. Renders as a pill in the panel topbar. */
export function WorkspaceSwitcher({ active, user }: Props) {
  const isAdmin = user?.role === "admin";

  return (
    <div
      role="tablist"
      className="inline-flex items-center gap-0.5 rounded-md border border-border bg-surface-muted/60 p-0.5"
    >
      <Tab
        href={URLS["match-day"]}
        active={active === "match-day"}
        icon={<Tv className="h-3.5 w-3.5" strokeWidth={2.5} />}
        label="Match Day"
      />
      {isAdmin && (
        <Tab
          href={URLS.admin}
          active={active === "admin"}
          icon={<ShieldCheck className="h-3.5 w-3.5" strokeWidth={2.5} />}
          label="Admin"
        />
      )}
    </div>
  );
}

function Tab({
  href,
  active,
  icon,
  label,
}: {
  href: string;
  active: boolean;
  icon: React.ReactNode;
  label: string;
}) {
  return (
    <a
      href={href}
      role="tab"
      aria-selected={active}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-[5px] px-3 h-7 text-[12.5px] transition-colors",
        active
          ? "bg-surface-elevated text-ink shadow-[inset_0_0_0_1px_var(--p-border-strong)]"
          : "text-ink-muted hover:text-ink",
      )}
    >
      {icon}
      {label}
    </a>
  );
}
